import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Clock, XCircle, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorKYCStatusScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    store
  } = useVendor();
  // Mock status based on taxId existence
  const status: 'approved' | 'pending' | 'rejected' = store.businessInfo.taxId ? 'approved' : 'pending';
  const timeline = [{
    title: 'Documents Submitted',
    date: 'Jan 15, 2024',
    completed: true
  }, {
    title: 'Under Review',
    date: status === 'pending' ? 'In Progress' : 'Jan 16, 2024',
    completed: status === 'approved' || status === 'rejected',
    current: status === 'pending'
  }, {
    title: status === 'rejected' ? 'Verification Failed' : 'Verified',
    date: status === 'approved' ? 'Jan 17, 2024' : status === 'rejected' ? 'Jan 17, 2024' : 'Pending',
    completed: status === 'approved' || status === 'rejected',
    current: status === 'approved' || status === 'rejected',
    isError: status === 'rejected'
  }];
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-10" data-id="element-2008">
      <Header title="KYC Status" showBack data-id="element-2009" />

      <div className="p-4 space-y-6" data-id="element-2010">
        {/* Status Header */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 text-center" data-id="element-2011">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${status === 'approved' ? 'bg-green-100 dark:bg-green-900/30' : status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-red-100 dark:bg-red-900/30'}`} data-id="element-2012">
            {status === 'approved' && <ShieldCheck className="w-12 h-12 text-green-600 dark:text-green-400" data-id="element-2013" />}
            {status === 'pending' && <Clock className="w-12 h-12 text-yellow-600 dark:text-yellow-400" data-id="element-2014" />}
            {status === 'rejected' && <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" data-id="element-2015" />}
          </motion.div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2016">
            {status === 'approved' ? 'KYC Approved' : status === 'pending' ? 'Under Review' : 'KYC Rejected'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2017">
            {status === 'approved' ? 'Your business identity has been fully verified. You have full access to all seller features.' : status === 'pending' ? 'Your documents are currently being reviewed by our compliance team. This usually takes 24-48 hours.' : 'We could not verify your business identity with the provided documents.'}
          </p>

          {status === 'rejected' && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl flex items-start gap-2 text-left" data-id="element-2018">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-2019" />
              <div data-id="element-2020">
                <p className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-2021">
                  Reason for rejection:
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1" data-id="element-2022">
                  The uploaded ID document is expired. Please upload a valid,
                  unexpired government-issued ID.
                </p>
              </div>
            </div>}
        </div>

        {/* Timeline */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2023">
          <h3 className="font-bold text-gray-900 dark:text-white mb-6" data-id="element-2024">
            Verification Timeline
          </h3>
          <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 dark:before:via-gray-700 before:to-transparent" data-id="element-2025">
            {timeline.map((step, idx) => <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2026">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 border-white dark:border-gray-800 shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm ${step.isError ? 'bg-red-500' : step.completed ? 'bg-green-500' : step.current ? 'bg-yellow-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-2027">
                  {step.isError ? <XCircle className="w-5 h-5 text-white" data-id="element-2028" /> : step.completed ? <CheckCircle2 className="w-5 h-5 text-white" data-id="element-2029" /> : step.current ? <Clock className="w-5 h-5 text-white" data-id="element-2030" /> : <div className="w-2 h-2 bg-white rounded-full" data-id="element-2031" />}
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 shadow-sm" data-id="element-2032">
                  <div className="flex items-center justify-between mb-1" data-id="element-2033">
                    <h4 className={`font-bold text-sm ${step.isError ? 'text-red-600 dark:text-red-400' : step.completed ? 'text-green-600 dark:text-green-400' : step.current ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-2034">
                      {step.title}
                    </h4>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2035">
                    {step.date}
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Actions */}
        <div className="pt-4" data-id="element-2036">
          {status === 'approved' ? <Button fullWidth onClick={() => navigate(Screen.VENDOR_KYC_EDIT)} className="bg-primary hover:bg-primary/90 text-white" data-id="element-2037">
              View KYC Details
            </Button> : status === 'rejected' ? <Button fullWidth onClick={() => navigate(Screen.VENDOR_KYC_EDIT)} className="bg-primary hover:bg-primary/90 text-white" data-id="element-2038">
              Resubmit KYC
            </Button> : <Button fullWidth variant="outline" onClick={() => navigate(Screen.VENDOR_PROFILE)} className="border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300" data-id="element-2039">
              Return to Profile
            </Button>}
        </div>
      </div>
    </div>;
}